<template>
  <div class="employment-history-component">
    <div
      class="service-header mb-3 d-flex align-items-center justify-content-between"
    >
      <div>
        <h6 class="fs-6 fw-semibold text-dark mb-1 mt-0">
          Employment History Check
        </h6>
        <p class="text-xs text-muted mb-0 lh-sm">
          Detailed employment verification including employer contact
          information and employment periods.
        </p>
      </div>
    </div>

    <!-- Multiple Employment History Entries -->
    <div
      v-for="(employment_entry, index) in employment_entries"
      :key="index"
      class="employment-entry mb-4"
    >
      <div
        class="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom"
      >
        <h6 class="fs-7 text-muted mb-0">
          Employment History Entry {{ index + 1 }}
        </h6>
        <button
          type="button"
          class="btn btn-bg-danger rounded-3 btn-sm text-xs"
          @click="remove_employment_entry(index)"
          v-if="employment_entries.length > 1"
        >
          <i class="fas fa-times"></i>
          Remove
        </button>
      </div>

      <div class="row g-2 mb-2">
        <div class="col-sm-12">
          <label class="form-label text-xs fw-medium text-secondary">
            In what region was this employment?
          </label>
          <div class="d-flex align-items-center">
            <div class="form-check me-3">
              <input
                class="form-check-input"
                type="radio"
                :name="'employment_region_' + index"
                value="South Africa"
                :id="'employment_region_sa_' + index"
                v-model="employment_entry.employment_region"
              />
              <label
                class="form-check-label text-xs"
                :for="'employment_region_sa_' + index"
                >South Africa</label
              >
            </div>
            <div class="form-check me-3">
              <input
                class="form-check-input"
                type="radio"
                :name="'employment_region_' + index"
                value="Africa"
                :id="'employment_region_africa_' + index"
                v-model="employment_entry.employment_region"
              />
              <label
                class="form-check-label text-xs"
                :for="'employment_region_africa_' + index"
                >Africa</label
              >
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                :name="'employment_region_' + index"
                value="Global"
                :id="'employment_region_global_' + index"
                v-model="employment_entry.employment_region"
              />
              <label
                class="form-check-label text-xs"
                :for="'employment_region_global_' + index"
                >Global</label
              >
            </div>
          </div>
        </div>
      </div>

      <!-- South Africa Employment History -->
      <div
        class="row g-2"
        v-if="employment_entry.employment_region === 'South Africa'"
      >
        <div class="col-sm-6">
          <!-- Employer -->
          <label class="form-label text-xs fw-medium text-secondary">
            Employer <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter employer name"
            v-model="employment_entry.employer"
            :id="`employer_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- Employment Type -->
          <label class="form-label text-xs fw-medium text-secondary">
            Employment Type <span class="text-danger">*</span>
          </label>
          <select
            class="form-select form-select-sm text-xs"
            v-model="employment_entry.employment_type_code"
            :id="`employment_type_code_${index}`"
          >
            <option value="">Select employment type</option>
            <option value="FT">Fulltime</option>
            <option value="CON">Contract</option>
            <option value="PT">Part-time</option>
            <option value="TEMP">Temporary</option>
          </select>
        </div>
        <div class="col-sm-6">
          <!-- Region/Province -->
          <label class="form-label text-xs fw-medium text-secondary">
            Region/Province <span class="text-danger">*</span>
          </label>
          <select
            class="form-select form-select-sm text-xs"
            v-model="employment_entry.region"
            :id="`region_${index}`"
          >
            <option value="">Select region</option>
            <option value="GAU">Gauteng</option>
            <option value="EC">Eastern Cape</option>
            <option value="KZN">KwaZulu-Natal</option>
            <option value="MPU">Mpumalanga</option>
            <option value="NW">North West Province</option>
            <option value="NC">Northern Cape</option>
            <option value="NP">Limpopo</option>
            <option value="FS">Free State</option>
            <option value="WC">Western Cape</option>
          </select>
        </div>
        <div class="col-sm-6">
          <!-- Department/Region Name (Optional) -->
          <label class="form-label text-xs fw-medium text-secondary">
            Department/Region Name (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter department or region"
            v-model="employment_entry.department_region_name"
          />
        </div>
        <div class="col-sm-6">
          <!-- Staff/Employee Number (Optional) -->
          <label class="form-label text-xs fw-medium text-secondary">
            Staff/Employee Number (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter employee number"
            v-model="employment_entry.staff_employee_number"
          />
        </div>
        <div class="col-sm-6">
          <!-- Start Date -->
          <label class="form-label text-xs fw-medium text-secondary">
            Start Date <span class="text-danger">*</span>
          </label>
          <input
            type="date"
            class="form-control form-control-sm text-xs"
            v-model="employment_entry.employment_start_date"
            :id="`employment_start_date_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- End Date -->
          <label class="form-label text-xs fw-medium text-secondary">
            End Date
          </label>
          <input
            type="date"
            class="form-control form-control-sm text-xs"
            v-model="employment_entry.employment_end_date"
            :disabled="employment_entry.currently_employed"
            :id="`employment_end_date_${index}`"
          />
        </div>
        <div class="col-sm-12">
          <!-- Currently employed here -->
          <input
            class="form-check-input"
            type="checkbox"
            :id="`currentlyEmployed_${index}`"
            v-model="employment_entry.currently_employed"
          />
          <label
            class="form-check-label text-xs"
            :for="`currentlyEmployed_${index}`"
          >
            Currently employed here
          </label>
        </div>
        <div class="col-sm-6">
          <!-- Last Position Held -->
          <label class="form-label text-xs fw-medium text-secondary">
            Last Position Held <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter position"
            v-model="employment_entry.last_position_held"
            :id="`last_position_held_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- Contact Person -->
          <label class="form-label text-xs fw-medium text-secondary">
            Contact Person <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter contact person name"
            v-model="employment_entry.contact_person"
            :id="`contact_person_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- Contact Number (Optional) -->
          <label class="form-label text-xs fw-medium text-secondary">
            Contact Number (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter contact number"
            v-model="employment_entry.contact_number"
            :id="`contact_number_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- Referee Name (Optional) -->
          <label class="form-label text-xs fw-medium text-secondary">
            Referee Name (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter referee name"
            v-model="employment_entry.referee_name"
          />
        </div>
        <div class="col-sm-6">
          <!-- Referee Contact (Optional) -->
          <label class="form-label text-xs fw-medium text-secondary">
            Referee Contact (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter referee contact"
            v-model="employment_entry.referee_contact_number"
          />
        </div>
      </div>

      <!-- Africa Employment History -->
      <div
        class="row g-2"
        v-else-if="employment_entry.employment_region === 'Africa'"
      >
        <div class="col-sm-6">
          <!-- Employer -->
          <label class="form-label text-xs fw-medium text-secondary">
            Employer <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter employer name"
            v-model="employment_entry.employer"
            :id="`employer_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- Employment Type -->
          <label class="form-label text-xs fw-medium text-secondary">
            Employment Type <span class="text-danger">*</span>
          </label>
          <select
            class="form-select form-select-sm text-xs"
            v-model="employment_entry.employment_type_code"
            :id="`employment_type_code_${index}`"
          >
            <option value="">Select employment type</option>
            <option value="FT">Fulltime</option>
            <option value="CON">Contract</option>
            <option value="PT">Part-time</option>
            <option value="TEMP">Temporary</option>
          </select>
        </div>
        <div class="col-sm-6">
          <!-- Country -->
          <label class="form-label text-xs fw-medium text-secondary">
            Country <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter country"
            v-model="employment_entry.country"
            :id="`country_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- Department/Region Name (Optional) -->
          <label class="form-label text-xs fw-medium text-secondary">
            Department/Region Name (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter department or region"
            v-model="employment_entry.department_region_name"
          />
        </div>
        <div class="col-sm-6">
          <!-- Staff/Employee Number (Optional) -->
          <label class="form-label text-xs fw-medium text-secondary">
            Staff/Employee Number (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter employee number"
            v-model="employment_entry.staff_employee_number"
          />
        </div>
        <div class="col-sm-6">
          <!-- Start Date -->
          <label class="form-label text-xs fw-medium text-secondary">
            Start Date <span class="text-danger">*</span>
          </label>
          <input
            type="date"
            class="form-control form-control-sm text-xs"
            v-model="employment_entry.employment_start_date"
            :id="`employment_start_date_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- End Date -->
          <label class="form-label text-xs fw-medium text-secondary">
            End Date
          </label>
          <input
            type="date"
            class="form-control form-control-sm text-xs"
            v-model="employment_entry.employment_end_date"
            :disabled="employment_entry.currently_employed"
            :id="`employment_end_date_${index}`"
          />
        </div>
        <div class="col-sm-12">
          <!-- Currently employed here -->
          <input
            class="form-check-input"
            type="checkbox"
            :id="`currentlyEmployed_${index}`"
            v-model="employment_entry.currently_employed"
          />
          <label
            class="form-check-label text-xs"
            :for="`currentlyEmployed_${index}`"
          >
            Currently employed here
          </label>
        </div>
        <div class="col-sm-6">
          <!-- Last Position Held -->
          <label class="form-label text-xs fw-medium text-secondary">
            Last Position Held <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter position"
            v-model="employment_entry.last_position_held"
            :id="`last_position_held_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- Contact Person -->
          <label class="form-label text-xs fw-medium text-secondary">
            Contact Person <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter contact person name"
            v-model="employment_entry.contact_person"
            :id="`contact_person_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- Contact Number -->
          <label class="form-label text-xs fw-medium text-secondary">
            Contact Number <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter contact number"
            v-model="employment_entry.contact_number"
            :id="`contact_number_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- Referee Name (Optional) -->
          <label class="form-label text-xs fw-medium text-secondary">
            Referee Name (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter referee name"
            v-model="employment_entry.referee_name"
          />
        </div>
        <div class="col-sm-6">
          <!-- Referee Contact (Optional) -->
          <label class="form-label text-xs fw-medium text-secondary">
            Referee Contact (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter referee contact"
            v-model="employment_entry.referee_contact_number"
          />
        </div>
      </div>

      <!-- Global Employment History -->
      <div
        class="row g-2"
        v-else-if="employment_entry.employment_region === 'Global'"
      >
        <div class="col-sm-6">
          <!-- Employer -->
          <label class="form-label text-xs fw-medium text-secondary">
            Employer <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter employer name"
            v-model="employment_entry.employer"
            :id="`employer_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- Employment Type -->
          <label class="form-label text-xs fw-medium text-secondary">
            Employment Type <span class="text-danger">*</span>
          </label>
          <select
            class="form-select form-select-sm text-xs"
            v-model="employment_entry.employment_type_code"
            :id="`employment_type_code_${index}`"
          >
            <option value="">Select employment type</option>
            <option value="FT">Fulltime</option>
            <option value="CON">Contract</option>
            <option value="PT">Part-time</option>
            <option value="TEMP">Temporary</option>
          </select>
        </div>
        <div class="col-sm-6">
          <!-- Country -->
          <label class="form-label text-xs fw-medium text-secondary">
            Country <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter country"
            v-model="employment_entry.country"
            :id="`country_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- Department/Region Name (Optional) -->
          <label class="form-label text-xs fw-medium text-secondary">
            Department/Region Name (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter department or region"
            v-model="employment_entry.department_region_name"
          />
        </div>
        <div class="col-sm-6">
          <!-- Staff/Employee Number (Optional) -->
          <label class="form-label text-xs fw-medium text-secondary">
            Staff/Employee Number (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter employee number"
            v-model="employment_entry.staff_employee_number"
          />
        </div>
        <div class="col-sm-6">
          <!-- Start Date -->
          <label class="form-label text-xs fw-medium text-secondary">
            Start Date <span class="text-danger">*</span>
          </label>
          <input
            type="date"
            class="form-control form-control-sm text-xs"
            v-model="employment_entry.employment_start_date"
            :id="`employment_start_date_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- End Date -->
          <label class="form-label text-xs fw-medium text-secondary">
            End Date
          </label>
          <input
            type="date"
            class="form-control form-control-sm text-xs"
            v-model="employment_entry.employment_end_date"
            :disabled="employment_entry.currently_employed"
            :id="`employment_end_date_${index}`"
          />
        </div>
        <div class="col-sm-12">
          <!-- Currently employed here -->
          <input
            class="form-check-input"
            type="checkbox"
            :id="`currentlyEmployed_${index}`"
            v-model="employment_entry.currently_employed"
          />
          <label
            class="form-check-label text-xs"
            :for="`currentlyEmployed_${index}`"
          >
            Currently employed here
          </label>
        </div>
        <div class="col-sm-6">
          <!-- Last Position Held -->
          <label class="form-label text-xs fw-medium text-secondary">
            Last Position Held <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter position"
            v-model="employment_entry.last_position_held"
            :id="`last_position_held_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- Contact Person -->
          <label class="form-label text-xs fw-medium text-secondary">
            Contact Person <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter contact person name"
            v-model="employment_entry.contact_person"
            :id="`contact_person_${index}`"
          />
        </div>
        <div class="col-sm-6">
          <!-- Contact Number -->
          <label class="form-label text-xs fw-medium text-secondary">
            Contact Number <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter contact number"
            v-model="employment_entry.contact_number"
            :id="`contact_number_${index}`"
          />
        </div>

        <div class="col-sm-6">
          <!-- Referee Name (Optional) -->
          <label class="form-label text-xs fw-medium text-secondary">
            Referee Name (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter referee name"
            v-model="employment_entry.referee_name"
          />
        </div>
        <div class="col-sm-6">
          <!-- Referee Contact (Optional) -->
          <label class="form-label text-xs fw-medium text-secondary">
            Referee Contact (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter referee contact"
            v-model="employment_entry.referee_contact_number"
          />
        </div>
      </div>
    </div>

    <!-- Add Employment Entry Button -->
    <div class="add-entry-section mt-3" v-if="employment_entries.length < 3">
      <button
        type="button"
        class="btn btn-bg-success rounded-3 btn-sm text-xs"
        @click="add_employment_entry"
      >
        <i class="fas fa-plus"></i>
        Add Another Employment History
      </button>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";
import Swal from "sweetalert2";

export default {
  name: "EmploymentHistory",
  props: {
    service_id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      employment_entries: [this.create_empty_employment_entry()],
    };
  },
  mounted() {
    this.fetch_data();
  },
  methods: {
    handle_data_submit() {
      if (this.validate_all_entries()) {
        this.employment_entries = this.employment_entries.map(entry => {
          const format_date_attribute = (date_str) => {
            if (!date_str) return "";
            // If already in DD/MM/YYYY, return as is
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(date_str)) return date_str;
            // If in YYYY-MM-DD, convert
            const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date_str);
            if (match) {
              return `${match[3]}/${match[2]}/${match[1]}`;
            }
            return date_str;
          };
          return {
            ...entry,
            currently_employed: entry.currently_employed ? "True" : "False",
            employment_start_date: format_date_attribute(entry.employment_start_date),
            employment_end_date: format_date_attribute(entry.employment_end_date),
          };
        });


        this.$emit("update_data", "employment", this.employment_entries);
        return true;
      }
      return false;
    },
    validate_all_entries() {
      for (let i = 0; i < this.employment_entries.length; i++) {
        if (!this.validate_employment_entry(this.employment_entries[i], i)) {
          return false;
        }
      }
      return true;
    },
    validate_employment_entry(entry, index) {
      // Remove previous error highlight
      const fields = [
        "employer",
        "employment_type_code",
        "region",
        "country",
        "employment_start_date",
        "last_position_held",
        "contact_person",
        "contact_number",
      ];
      fields.forEach((field) => {
        const el = document.getElementById(`${field}_${index}`);
        if (el) el.classList.remove("border-danger");
      });

      // South Africa validation
      if (entry.employment_region === "South Africa") {
        if (!entry.employer) {
          const el = document.getElementById(`employer_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Employer is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.employment_type_code) {
          const el = document.getElementById(`employment_type_code_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Employment type is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.region) {
          const el = document.getElementById(`region_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Region/Province is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.employment_start_date) {
          const el = document.getElementById(`employment_start_date_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Start date is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.last_position_held) {
          const el = document.getElementById(`last_position_held_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Last position held is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.contact_person) {
          const el = document.getElementById(`contact_person_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Contact person is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
      }
      // Africa validation
      else if (entry.employment_region === "Africa") {
        if (!entry.employer) {
          const el = document.getElementById(`employer_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Employer is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.employment_type_code) {
          const el = document.getElementById(`employment_type_code_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Employment type is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.country) {
          const el = document.getElementById(`country_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Country is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.employment_start_date) {
          const el = document.getElementById(`employment_start_date_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Start date is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.last_position_held) {
          const el = document.getElementById(`last_position_held_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Last position held is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.contact_person) {
          const el = document.getElementById(`contact_person_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Contact person is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.contact_number) {
          const el = document.getElementById(`contact_number_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Contact number is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
      }
      // Global validation
      else if (entry.employment_region === "Global") {
        if (!entry.employer) {
          const el = document.getElementById(`employer_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Employer is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.employment_type_code) {
          const el = document.getElementById(`employment_type_code_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Employment type is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.country) {
          const el = document.getElementById(`country_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Country is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.employment_start_date) {
          const el = document.getElementById(`employment_start_date_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Start date is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.last_position_held) {
          const el = document.getElementById(`last_position_held_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Last position held is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.contact_person) {
          const el = document.getElementById(`contact_person_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Contact person is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
        if (!entry.contact_number) {
          const el = document.getElementById(`contact_number_${index}`);
          if (el) el.classList.add("border-danger");
          Swal.fire({
            icon: "info",
            title: "Validation Error",
            text: "Contact number is required.",
            timer: 2500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          return false;
        }
      }
      return true;
    },
    create_empty_employment_entry() {
      return {
        id: "",
        employment_region: "",
        country: "",
        employer: "",
        employment_type_code: "",
        employment_start_date: "",
        employment_end_date: "",
        last_position_held: "",
        currently_employed: false,
        contact_person: "",
        contact_number: "",
        region: "",
        department_region_name: "",
        referee_name: "",
        referee_contact_number: "",
        staff_employee_number: "",
      };
    },

    add_employment_entry() {
      if (this.employment_entries.length < 2) {
        this.employment_entries.push(this.create_empty_employment_entry());
      }
    },

    async remove_employment_entry(index) {
      const entry = this.employment_entries[index];
      if (entry.id) {
        const confirmed = await Swal.fire({
          icon: "warning",
          title: "Are you sure?",
          text: "This will permanently remove the employment entry and cannot be undone.",
          showCancelButton: true,
          confirmButtonText: "Yes, remove it",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
        });
        if (!confirmed.isConfirmed) return;

        const token = localStorage.getItem("token");
        try {
          const response = await fetch(`${API_BASE_URL}/verify_api/employee_screening/api/keys/remove/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Token ${token}`,
            },
            body: JSON.stringify({ employee_screening_service_key_group_id: entry.id }),
          });
          const data = await response.json();
          if (data.status === "success") {
            if (this.employment_entries.length > 1) {
              this.employment_entries.splice(index, 1);
            }
            Swal.fire({
              icon: "success",
              title: "Removed",
              text: "Employment entry removed successfully.",
              timer: 2000,
              showConfirmButton: false,
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: data.message || "Failed to remove employment entry.",
            });
          }
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to remove employment entry.",
          });
        }
      } else {
        if (this.employment_entries.length > 1) {
          this.employment_entries.splice(index, 1);
        }
      }
    },
    fetch_data() {
      const token = localStorage.getItem("token");
      const workflow_id = localStorage.getItem("workflow_id") || null;
      if (!workflow_id) {
        console.error("Workflow ID is not set in localStorage.");
        return;
      }

      const params = new URLSearchParams({
        workflow_id: workflow_id,
        service_id: this.service_id,
      });

      fetch(
        `${API_BASE_URL}/verify_api/employee_screening/api/get_sections_by_workflow_client/?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        }
      )
        .then((response) => response.json())
        .then((data) => {
          if (data.status === "success" && data.data) {
            const assigned_services = data.data.sections || [];
            const service =
              assigned_services.length > 0 ? assigned_services[0] : null;
            if (
              service &&
              service.key_groups &&
              Array.isArray(service.key_groups) &&
              service.key_groups.length > 0
            ) {
              // Map each key_group to an employment_entry
              this.employment_entries = service.key_groups.map((key_group) => {
                // Flatten key_mappings into an object
                let entry = {};
                if (key_group.key_mappings && Array.isArray(key_group.key_mappings)) {
                  key_group.key_mappings.forEach(mapping => {
                    entry[mapping.key_name] = mapping.key_value;
                  });
                }

                let category = key_group.category || {};
                let employment_region = "";

                if (category.category_name) {
                  if (category.category_name === "South African Only") {
                    employment_region = "South Africa";
                  } else if (category.category_name === "Africa") {
                    employment_region = "Africa";
                  } else if (category.category_name === "Global") {
                    employment_region = "Global";
                  }
                }

                return {
                  id: key_group.id || "",
                  employment_region: employment_region,
                  country: entry.country || "",
                  employer: entry.employer || "",
                  employment_type_code: entry.employment_type_code || "",
                  employment_start_date: entry.employment_start_date || "",
                  employment_end_date: entry.employment_end_date || "",
                  last_position_held: entry.last_position_held || "",
                  currently_employed: entry.currently_employed === "True" ? true : false,
                  contact_person: entry.contact_person || "",
                  contact_number: entry.contact_number || "",
                  region: entry.region || "",
                  department_region_name: entry.department_region_name || "",
                  referee_name: entry.referee_name || "",
                  referee_contact_number: entry.referee_contact_number || "",
                  staff_employee_number: entry.staff_employee_number || "",
                };
              });
            } else {
              // If no key_groups, reset to one empty entry
              this.employment_entries = [this.create_empty_employment_entry()];
            }
          } else {
            this.employment_entries = [this.create_empty_employment_entry()];
          }
        })
        .catch((error) => {
          console.error("Error fetching employment history:", error);
        });
    },
  },
  watch: {
    employment_entries: {
      handler(new_entries) {
        // Check for currently_employed changes and clear end dates
        new_entries.forEach((entry, index) => {
          if (entry.currently_employed === true && entry.employment_end_date) {
            this.employment_entries[index].employment_end_date = "";
          }
        });
      },
      deep: true,
    },
  },
};
</script>
<style scoped>
.fs-7 {
  font-size: 0.875rem;
}

.service-header {
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f1f3f4;
}

.form-label {
  margin-bottom: 0.25rem;
}

.form-control-sm {
  padding: 0.375rem 0.5rem;
}

.employment-entry {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
}

.add-entry-section {
  text-align: center;
}
</style>
